import type { ContractTypeCanHaveEndDate } from '@libs/data-access/entities'
import { ContractTypeCanHaveEndDateArray } from '@libs/data-access/entities'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import moment from 'moment'
import * as yup from 'yup'

import { useYupFixedPhoneValidator } from './validations/useYupFixedPhoneValidator'
import { useYupMobilePhoneValidator } from './validations/useYupMobilePhoneValidator'

export const useNewUserFormValidation = ({ country }: { country: string }) => {
  const { t, tCommon, tForms } = usePlanningFormTranslation()

  return yup.object().shape({
    firstname: yup
      .string()
      .nullable()
      .required(tCommon('form.errors.fieldIsRequired')),
    lastname: yup
      .string()
      .nullable()
      .required(tCommon('form.errors.fieldIsRequired')),
    birth_name: yup
      .string()
      .nullable()
      .required(tCommon('form.errors.fieldIsRequired')),
    birth_date: yup
      .string()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .nullable()
      .test(
        'birth_date_min_date',
        tForms('commonForms.forms.user.invalid.birthDateMinMax'),
        function (birth_date) {
          if (!birth_date) {
            return true
          }
          return moment(birth_date).isAfter(moment('1899-12-31'))
        }
      )
      .test(
        'birth_date_max_date',
        tForms('commonForms.forms.user.invalid.birthDateMinMax'),
        function (birth_date) {
          if (!birth_date) {
            return true
          }
          return moment(birth_date).isBefore(moment())
        }
      ),
    contract_time: yup
      .number()
      .transform(value => (Number.isNaN(value) ? undefined : value))
      .nullable()
      .lessThan(80, t('membershipCreation.validations.time.max')),
    contract_start: yup
      .date()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .nullable()
      .min(
        new Date(1900, 0, 1),
        t('membershipCreation.validations.startDate.minMaxDate')
      )
      .max(
        new Date(2100, 12, 31),
        t('membershipCreation.validations.startDate.minMaxDate')
      ),
    contract_end: yup
      .date()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .nullable()
      .when('contract_type', {
        is: value => {
          const isFijoContractType = country === 'ES' && value === 'seasonal'

          return (
            ContractTypeCanHaveEndDateArray.includes(
              value as ContractTypeCanHaveEndDate
            ) && !isFijoContractType
          )
        },
        then: yup
          .date()
          .nullable()
          .required(tCommon('form.errors.fieldIsRequired'))
          .min(
            new Date(1900, 0, 1),
            t('membershipCreation.validations.endDate.minMaxDate')
          )
          .max(
            new Date(2100, 12, 31),
            t('membershipCreation.validations.endDate.minMaxDate')
          )
          .test(
            'new_end_after_start',
            t('membershipCreation.validations.endDate.beforeStartDate'),
            function () {
              const { contract_end, contract_start } = this.parent

              if (contract_start && contract_end) {
                return moment(contract_end).isSameOrAfter(
                  moment(contract_start)
                )
              }
              return true
            }
          ),
        otherwise: schema => {
          return schema.nullable()
        }
      }),
    email: yup.string().email(tForms('commonForms.forms.user.invalid.email')),
    mobile_phone: useYupMobilePhoneValidator(),
    fixed_phone: useYupFixedPhoneValidator()
  })
}
